<template>
  <div
    v-if="!userIsBanned"
    ref="discussion-container"
    class="discussions-component-container"
  >
    <div class="total">
      <span class="total-text">{{ totalText }}</span>
      <span class="sort-label">Sort by</span>
      <listbox
        :options="sortOptions"
        :value="sortOrder"
        @change="updateSortOrder"
      />
    </div>
    <discussion-editor
      :active="activeInput === 'discussions-root'"
      :input-key="'discussions-root'"
      @cancel="setInput(null)"
      @create-discussion="create"
      @input-active="setInput('discussions-root')"
    />
    <discussion
      v-for="discussion in rootDiscussions"
      :ref="'discussion-' + discussion.id"
      :key="discussion.id"
      :active-input="activeInput"
      :child-discussion-count="childDiscussions(discussion).length"
      :discussion="discussion"
      :user="user"
      :class="{featured: featuredDiscussion(discussion)}"
      @create-discussion="create"
      @edit-discussion="edit"
      @set-input="setInput"
      @expanded-replies="expandedReplies"
      @trigger-delete="triggerDeleteModal"
      @followed-discussion="followedDiscussion"
      @unfollowed-discussion="unfollowedDiscussion"
    >
      <template #rating>
        <discussion-rating
          :asset="flashcard"
          :category="category"
          :discussion="discussion"
        />
      </template>
      <template #replies>
        <discussion
          v-for="childDiscussion in childDiscussions(discussion)"
          :key="childDiscussion.id"
          :active-input="activeInput"
          :discussion="childDiscussion"
          :parent-discussion="discussion"
          :user="user"
          @create-discussion="create"
          @edit-discussion="edit"
          @set-input="setInput"
          @trigger-delete="triggerDeleteModal"
        >
          <template #rating>
            <discussion-rating
              :asset="flashcard"
              :category="category"
              :discussion="childDiscussion"
              :parent-discussion="discussion"
            />
          </template>
        </discussion>
      </template>
    </discussion>
    <discussion-delete-modal
      ref="modal"
      @delete-discussion="deleteDiscussion"
    />
  </div>
</template>

<script>
import Discussion from '@/components/discussions/discussion.vue';
import DiscussionDeleteModal from '@/components/modals/discussion-delete.vue';
import DiscussionEditor from '@/components/discussions/discussion-editor.vue';
import DiscussionRating from '@/components/discussions/discussion-rating.vue';
import Listbox from '@/components/singletons/listbox.vue';

const SORT_ORDER = {
  SCORE: 'SCORE',
  LATEST: 'LATEST',
};

export default {
  components: {
    Discussion,
    DiscussionEditor,
    DiscussionDeleteModal,
    DiscussionRating,
    Listbox,
  },
  props: {
    category: {
      type: Object,
    },
    flashcard: {
      type: Object,
      default: () => ({}),
    },
    user: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      activeInput: null,
      discussions: [],
      expanded: {},
      loading: true,
      sortOrder: SORT_ORDER.SCORE,
      submitting: false,
    };
  },
  computed: {
    rootDiscussions() {
      const roots = this.discussions.filter((d) => d.path === `${d.id}`);
      return this.sortDiscussions(roots);
    },
    sortLabels() {
      return {
        [SORT_ORDER.SCORE]: 'Most Popular',
        [SORT_ORDER.LATEST]: 'Most Recent',
      };
    },
    sortOptions() {
      return Object.values(SORT_ORDER).map((order) => ({
        text: this.sortLabels[order],
        value: order,
      }));
    },
    totalText() {
      if (this.loading) {
        return 'Loading Discussions...';
      } else {
        let countText =
          this.discussions.length === 1 ? 'Discussion' : 'Discussions';
        return `${this.discussions.length} ${countText}`;
      }
    },
    userIsBanned() {
      return this.user?.bannedFromDiscussions;
    },
  },
  watch: {
    flashcard(oldFlashcard, newFlashcard) {
      if (oldFlashcard.id !== newFlashcard.id) {
        this.updateDiscussions();
      }
   },
  },
  mounted() {
    this.updateDiscussions();
  },
  methods: {
    childDiscussions(root) {
      const children = this.discussions.filter((d) => {
        const ids = d.path.split('.');
        return ids.length > 1 && ids[0] === `${root.id}`;
      });
      return this.sortDiscussions(children);
    },
    create({ message, parentDiscussionId }) {
      this.submitting = true;

      let params = {
        flashcardId: this.$route.params.flashcardId,
        message: message,
        parentDiscussionId: parentDiscussionId ? parentDiscussionId : null,
      };

      const isQOTD = this.$route.path.includes('/qotd');

      this.$analytics.events.createdDiscussionComment(
        this.flashcard,
        !!parentDiscussionId,
        isQOTD,
      );
      this.$store
        .dispatch('discussions/createDiscussion', params)
        .then((discussion) => {
          this.discussions.push(discussion);
          this.createDiscussionUpvote(discussion);
          this.activeInput = null;
          // scroll to the just posted comment
          this.$nextTick( () => {
            const discussionRef =  this.$refs[`discussion-${discussion.id}`];
            if( discussionRef ) {
              discussionRef[0].$el.scrollIntoView({ behavior: 'smooth' });
            }
          });
        })
        .catch((error) => {
          return Promise.reject(error);
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    createDiscussionUpvote(discussion) {
      const rating = {
        rating: 1,
        ratingType: 'updown',
        rateableId: discussion.id,
        rateableType: 'Discussion',
      };
      this.$store.commit('ratings/set', [rating]);
    },
    deleteDiscussion(discussion) {
      const filteredDiscussions = this.discussions.filter((d) => {
        // Remove the discussion if it is the deleted discussion
        if (d.id === discussion.id) {
          return false;
        }

        // Remove the discussion if the parent is the deleted discussion
        const path = d.path.split('.');
        const parent = path[path.length - 2]; // Path will typically be [1, 2] so path[path.length - 2] => path[2 - 2] => path[0]
        if (Number(parent) === discussion.id) {
          return false;
        }

        return true;
      });
      this.discussions = filteredDiscussions;
    },
    edit({ message, discussionId }) {
      this.submitting = true;
      const params = {
        id: discussionId,
        message: message,
      };

      this.$store
        .dispatch('discussions/updateDiscussion', params)
        .then((discussion) => {
          this.activeInput = null;
          const filteredDiscussions = this.discussions.filter(
            (d) => d.id !== discussion.id,
          );
          this.discussions = [...filteredDiscussions, discussion];
        })
        .catch((error) => {
          return Promise.reject(error);
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    expandedReplies() {
      const source = {
        type: this.flashcard?.id ? 'Flashcard' : undefined,
        id: this.flashcard?.id,
        category: this.category?.name,
        categoryId: this.category?.id,
      };
      this.$analytics.events.viewedDiscussionReplies(source);
    },
    featuredDiscussion(discussion) {
      if( this.$route.query.discussionId ) {
        const discussionId = this.$route.query.discussionId;
        const childDiscussionIds = this.childDiscussions(discussion).map((d) => d.id);
        return childDiscussionIds.includes(discussionId) || discussionId == discussion.id;
      }
    },
    fetchDiscussionRatings() {
      const discussionIds = this.discussions.map(d => d.id);
      this.$store.dispatch('ratings/fetch', { id: discussionIds, type: 'Discussion' });
    },
    followedDiscussion(discussion) {
      const d = this.discussions.find( d => d.id == discussion.id );
      if( d ) {
        d.following = true;
      }
    },
    unfollowedDiscussion(discussion) {
      const d = this.discussions.find( d => d.id == discussion.id );
      if( d ) {
        d.following = false;
      }
    },
    setInput(inputKey) {
      this.activeInput = inputKey;
    },
    sortDiscussions(discussions) {
      if (this.sortOrder === SORT_ORDER.SCORE) {
        return discussions.sort((a, b) => b.sortOrder - a.sortOrder);
      } else if (this.sortOrder === SORT_ORDER.LATEST) {
        return discussions.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
      }
    },
    triggerDeleteModal(discussion) {
      this.$modal.show('discussion-delete-modal', { discussion: discussion });
    },
    updateDiscussions() {
      const params = {
        flashcardId: this.flashcard.id,
      };

      this.$store
        .dispatch('discussions/fetchDiscussions', params)
        .then((discussions) => {
          this.discussions = discussions;
          this.fetchDiscussionRatings();
          this.loading = false;
          if (this.$route.query.discussionId) {
            let discussion = this.discussions.find(
              (d) => d.id == this.$route.query.discussionId,
            );
            const parentId = discussion.path.split('.')[0];
            if( parseInt(parentId) != discussion.id ) {
              discussion = this.discussions.find(
                (d) => d.id == parentId,
              );
            }

            this.$nextTick(function () {
              const discussionContainer = this.$refs['discussion-container'];
              if( discussionContainer ) {
                discussionContainer.scrollIntoView({ behavior: 'smooth' });
              }
              if (discussion) {
                const discussionRef =  this.$refs[`discussion-${discussion.id}`];
                if( discussionRef ) {
                  discussionRef[0].$el.scrollIntoView({ behavior: 'smooth' });
                }
              }
            });
          }
        })
        .catch((error) => {
          this.loading = false;
          return Promise.reject(error);
        });
    },
    updateSortOrder(order) {
      this.sortOrder = order;
    },
  },
};
</script>

<style lang="scss" scoped>
.discussions-component-container {
  background: #f2f4f7;
  margin: 0 rem-calc(-48) 0 rem-calc(-48);
  padding: rem-calc(48);

  .total {
    display: flex;
    align-items: center;

    .total-text {
      color: #12263b;
    }

    .sort-label {
      color: #767676;
      font-size: rem-calc(14);
      margin-left: auto;
      margin-right: rem-calc(12);
    }

    margin-bottom: rem-calc(12);
  }
  .featured {
    background-color: lightgoldenrodyellow;
  }
}
</style>
